import { useEffect, useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { resolveImageUrl } from '../components/ImageUpload';
import InStoryAd from '../components/InStoryAd'; // eslint-disable-line no-unused-vars
import { useChannel } from '../contexts/ChannelContext';
import ShareToStoryButton from '../components/ShareToStoryButton';
import SaveButton from '../components/SaveButton';
import ReportBlockMenu from '../components/ReportBlockMenu';
import BrandIntegration from '../components/BrandIntegration';
import FollowButton from '../components/FollowButton';
import { trackStoryView, trackStoryCompleted } from '../lib/analytics';
import CardSaveOverlay from '../components/CardSaveOverlay';
import ContinueReadingBeacon from '../components/ContinueReadingBeacon';
import PushReminderPrompt from '../components/PushReminderPrompt';
import SEOHead from '../components/SEOHead';
import ContentBlockRenderer from '../components/story/ContentBlockRenderer';
import FounderStoryStats from '../components/FounderStoryStats';
import useDocumentHead from '../hooks/useDocumentHead';
import { toVideoEmbed, isDirectVideo } from '../utils/videoEmbed';

const SHARE_DOMAIN = 'flythouse.com';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// iter234 · Story pages render dark now. Pasted article HTML (Claude/Docs)
// often carries light-theme inline styles — white stat boxes, near-black
// text, pale gray borders. Remap those so embedded cards / ads / links sit
// naturally on the black canvas while intentional accent colors survive.
const parseCssColor = (value) => {
  if (!value) return null;
  const v = value.trim().toLowerCase();
  if (v === 'white') return [255, 255, 255];
  if (v === 'black') return [0, 0, 0];
  const hex = v.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/);
  if (hex) {
    let h = hex[1];
    if (h.length === 3) h = h.split('').map((c) => c + c).join('');
    return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
  }
  const rgb = v.match(/^rgba?\(\s*(\d+)[,\s]+(\d+)[,\s]+(\d+)/);
  if (rgb) return [Number(rgb[1]), Number(rgb[2]), Number(rgb[3])];
  return null;
};
const luminance = ([r, g, b]) => (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
const isNeutral = ([r, g, b]) => Math.max(r, g, b) - Math.min(r, g, b) < 60;

const darkenInlineStyles = (html) => {
  if (!html || typeof window === 'undefined') return html;
  try {
    const doc = new DOMParser().parseFromString(`<div id="__dark-root">${html}</div>`, 'text/html');
    doc.querySelectorAll('[style]').forEach((el) => {
      const s = el.style;
      const bg = parseCssColor(s.backgroundColor);
      if (bg && luminance(bg) > 0.55) s.backgroundColor = 'rgba(255,255,255,0.06)';
      // `background:` shorthand (pasted templates often use it instead of background-color)
      if (!bg && s.background) {
        const bgSh = parseCssColor(s.background);
        if (bgSh && luminance(bgSh) > 0.55) s.background = 'rgba(255,255,255,0.06)';
      }
      const fg = parseCssColor(s.color);
      if (fg && luminance(fg) < 0.42 && isNeutral(fg)) s.color = '#f5f5f7';
      ['borderColor', 'borderTopColor', 'borderRightColor', 'borderBottomColor', 'borderLeftColor'].forEach((k) => {
        const c = parseCssColor(s[k]);
        if (c && luminance(c) > 0.55 && isNeutral(c)) s[k] = 'rgba(255,255,255,0.14)';
      });
      if (s.boxShadow && s.boxShadow !== 'none') s.boxShadow = 'none';
    });
    return doc.getElementById('__dark-root').innerHTML;
  } catch {
    return html;
  }
};

const StoryPage = () => {
  const { slug } = useParams();
  const channel = useChannel();
  const isVisionary = channel.id === 'dreams_visionary';
  const [story, setStory] = useState(null);
  const [relatedStories, setRelatedStories] = useState([]);
  const [loading, setLoading] = useState(true);

  // iter151 · In-story ad slots disabled globally. The fetch + state
  // are gone so the story page never even asks the backend for them.
  // Story editor still lets the founder attach slots for a future
  // re-enable, but nothing renders below.
  const [networkAthlete, setNetworkAthlete] = useState(null);

  // Track when the reader hits the bottom of the article (= story completed).
  // Fires at most once per story view; arrival time logged so we can record
  // approximate read duration.
  useEffect(() => {
    if (!story?.id) return undefined;
    const startedAt = Date.now();
    let fired = false;
    const handler = () => {
      if (fired) return;
      const scrollPct = (window.scrollY + window.innerHeight) /
        Math.max(document.documentElement.scrollHeight, 1);
      if (scrollPct >= 0.85) {
        fired = true;
        trackStoryCompleted(story, Math.round((Date.now() - startedAt) / 1000));
      }
    };
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, [story?.id]);

  useEffect(() => {
    const fetchStory = async () => {
      setLoading(true);
      try {
        // iter270 · Consume the index.html deep-link prefetch — the story
        // JSON started downloading in parallel with the JS bundle, so on
        // cold mobile loads we skip an entire network round-trip here.
        const pf = (typeof window !== 'undefined' && window.__FLYT_PREFETCH && window.__FLYT_PREFETCH.path === `/stories/${slug}`)
          ? window.__FLYT_PREFETCH.promise : null;
        if (pf) window.__FLYT_PREFETCH = null;
        const [storyRes, allStories] = await Promise.all([
          pf ? pf.then((d) => ({ data: d })).catch(() => axios.get(`${API}/stories/${slug}`)) : axios.get(`${API}/stories/${slug}`),
          axios.get(`${API}/stories?limit=4&channel=${channel.id}`)
        ]);
        setStory(storyRes.data);
        // Fire PostHog story_viewed once the story payload lands.
        trackStoryView(storyRes.data);
        setRelatedStories(allStories.data.filter(s => s.slug !== slug).slice(0, 3));
        // Look up if any athlete/visionary on the network references this story
        try {
          const lookupEndpoint = isVisionary ? `${API}/visionaries` : `${API}/athletes`;
          const aRes = await axios.get(lookupEndpoint);
          // Visionaries don't have network_active flag, athletes do
          const match = (aRes.data || []).find(a =>
            (isVisionary || a.network_active) && a.network_story_slug === slug
          );
          setNetworkAthlete(match || null);
      } catch (e) { console.debug('[StoryPage] network athlete lookup skipped:', e?.message); }
      } catch (error) {
        console.error('Error fetching story:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStory();
  }, [slug, channel.id, isVisionary]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    } catch {
      return '';
    }
  };

  // Pasted designed templates reference Google Fonts via <link> tags that
  // DOMPurify (rightly) strips. Hoist those hrefs into <head> so the
  // template's typography (Bebas Neue, Barlow, …) still loads.
  useEffect(() => {
    if (!story?.article_body) return undefined;
    const hrefs = [...story.article_body.matchAll(/<link[^>]+href=["'](https:\/\/fonts\.googleapis\.com\/[^"']+)["']/gi)]
      .map((m) => m[1]);
    const added = [];
    hrefs.forEach((href) => {
      if ([...document.head.querySelectorAll('link[rel="stylesheet"]')].some((l) => l.href === href)) return;
      const l = document.createElement('link');
      l.rel = 'stylesheet';
      l.href = href;
      document.head.appendChild(l);
      added.push(l);
    });
    return () => added.forEach((l) => l.remove());
  }, [story?.article_body]);

  const sanitizedArticleBody = useMemo(() => {
    if (!story?.article_body) return '';
    // Self-heal: older saves of full-document pastes were wrapped in <p>
    // with <br /> at every newline (renders blank). Unwrap before sanitizing.
    let raw = story.article_body;
    const low = raw.trimStart().toLowerCase();
    if (low.startsWith('<p><!doctype') || low.startsWith('<p><html')) {
      raw = raw
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\/p>\s*<p>/gi, '\n\n')
        .replace(/^\s*<p>/i, '')
        .replace(/<\/p>\s*$/i, '');
    }
    return darkenInlineStyles(DOMPurify.sanitize(raw, {      ALLOWED_TAGS: [
        // Headings + basic text
        'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
        'p', 'strong', 'em', 'b', 'i', 'u', 's', 'small', 'sub', 'sup', 'mark',
        'br', 'hr', 'span', 'div', 'section', 'article', 'aside',
        // Links + lists
        'a', 'ul', 'ol', 'li', 'blockquote', 'pre', 'code',
        // Images / figures
        'img', 'figure', 'figcaption', 'picture', 'source',
        // Rich media — iframes (YouTube/Vimeo/Twitter), native video/audio
        'iframe', 'video', 'audio', 'track',
        // Tables (scoreboards / recaps)
        'table', 'thead', 'tbody', 'tfoot', 'tr', 'td', 'th', 'caption', 'colgroup', 'col',
      ],
      ALLOWED_ATTR: [
        // Common
        'href', 'src', 'alt', 'class', 'id', 'title', 'target', 'rel', 'style',
        'width', 'height', 'loading', 'decoding',
        // Images
        'srcset', 'sizes',
        // Table
        'colspan', 'rowspan', 'scope',
        // Media
        'controls', 'autoplay', 'muted', 'loop', 'playsinline', 'poster', 'preload',
        // Iframes (YT/Vimeo/Twitter embeds)
        'allow', 'allowfullscreen', 'frameborder', 'referrerpolicy', 'sandbox',
      ],
      ALLOW_DATA_ATTR: true,
      // Permit common embed hosts. Anything pointing somewhere safe will pass; suspicious URIs still get filtered.
      ADD_URI_SAFE_ATTR: ['allow', 'allowfullscreen'],
    }));
  }, [story?.article_body]);

  // ── Dynamic document head ──────────────────────────────────────────
  // Sets <title> + og:* + twitter:* meta tags so the story title (not the
  // generic site title) shows up in tab titles AND in link previewers
  // that execute JS before snapshotting (iMessage iOS 16+, Telegram,
  // Slack, etc.). Non-JS crawlers fall back to the OG-rich
  // /api/share/stories/{slug} URL that our in-app share button uses.
  const headSiteName =
    channel.id === 'flyt' ? 'FLYT Studios'
      : channel.id === 'dreams_visionary' ? 'Dreams Visionary'
        : 'Dreams League';
  const headDescRaw =
    (story?.subtitle && story?.subtitle !== story?.name && story.subtitle) ||
    (story?.summary || '');
  const headDescription = headDescRaw
    ? (headDescRaw.length > 200 ? headDescRaw.slice(0, 197).trimEnd() + '…' : headDescRaw)
    : `A ${headSiteName} story by ${story?.author || 'Jay Pernell'}.`;
  const headRawImg = story?.cover_image || story?.thumbnail;
  const headImage = headRawImg && (headRawImg.startsWith('http://') || headRawImg.startsWith('https://'))
    ? headRawImg
    : headRawImg ? `${BACKEND_URL}${headRawImg}` : undefined;
  const headUrl = typeof window !== 'undefined' ? window.location.href : undefined;
  useDocumentHead({
    title: story?.name,
    description: headDescription,
    image: headImage,
    url: headUrl,
    siteName: headSiteName,
    type: 'article',
  });

  if (loading) {
    return <div className="min-h-screen bg-[#050506]"><div className="w-full h-96 skeleton"></div></div>;
  }

  if (!story) {
    return (
      <div className="min-h-screen bg-[#050506] text-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-display text-4xl mb-4">Story Not Found</h1>
          <Link to={channel.storiesPath} className="btn-pill bg-white text-black inline-block">Back to Stories</Link>
        </div>
      </div>
    );
  }

  // shareUrl / shareText removed alongside the bottom social-share buttons.
  // Top-of-article share controls handle their own URL building.
  const gallery = story.gallery || [];

  return (
    <div data-testid="story-page" className="story-dark bg-[#050506] text-white min-h-screen">
      {/* Per-story canonical + unique meta description so every
          /stories/{slug} page has its own SEO surface. */}
      <SEOHead
        title={`${story.name || 'Story'} · Flythouse Stories`}
        description={(story.subtitle || story.excerpt || story.name || '').slice(0, 200) || undefined}
        image={resolveImageUrl(story.cover_image || story.thumbnail) || undefined}
      />
      <ContinueReadingBeacon
        slug={story.slug}
        title={story.name}
        channelLabel={channel.label}
        url={`${channel.storiesPath}/${story.slug}`}
        image={resolveImageUrl(story.cover_image || story.thumbnail)}
        targetSelector='[data-testid="article-body"]'
      />
      <PushReminderPrompt
        targetSelector='[data-testid="article-body"]'
        userId={null}
      />
      {/* Article Header */}
      <article className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-8">
        <p className="font-body text-sm text-white/50 uppercase tracking-wider mb-4">FLYT Studios</p>
        <h1 className="font-display text-5xl md:text-6xl lg:text-7xl mb-8 uppercase leading-[1.05]" data-testid="story-title">
          {story.name}
        </h1>
        <FounderStoryStats slug={story.slug || slug} />
      </article>

      {/* Hero Image with optional overlay */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mb-4">
        <div className="relative">
          <img
            loading="eager"
            decoding="async"
            src={resolveImageUrl(story.cover_image)}
            alt={story.name}
            fetchPriority="high"
            className="w-full h-auto rounded-lg object-cover"
            style={{ maxHeight: '600px' }}
          />
          {story.secondary_image && (
            <div className="absolute bottom-4 right-4 w-1/3 max-w-[220px] hidden md:block">
              <img 
                src={resolveImageUrl(story.secondary_image, 400)} 
                alt="Secondary" 
                loading="lazy"
                decoding="async"
                className="w-full h-auto rounded-lg shadow-xl border-4 border-white/15"
              />
            </div>
          )}
        </div>

        {/* iter506 · Photo credit sub-caption — small, italicized line
            directly under the hero image (license attribution style). */}
        {story.photo_credit && (
          <p
            className="mt-2 text-xs md:text-sm text-white/50 italic tracking-wide"
            data-testid="story-photo-credit"
          >
            {story.photo_credit}
          </p>
        )}
        
        {/* Byline + summary + share */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mt-6 pb-8 border-b border-white/10">
          <div className="md:col-span-3">
            <p className="font-body text-base text-white/80 leading-relaxed" data-testid="story-summary">
              {story.summary}
            </p>
            <div className="mt-5 flex flex-wrap items-center gap-3">
              <ShareToStoryButton
                title={story.name}
                subtitle={story.subtitle || story.summary}
                category={story.category}
                image={resolveImageUrl(story.cover_image || story.thumbnail)}
                url={`https://${SHARE_DOMAIN}${channel.storiesPath}/${story.slug}`}
                slug={story.slug}
                channelId={channel.id}
              />
              <SaveButton type="stories" id={story.id} variant="pill" theme="dark" />
              {networkAthlete && !isVisionary && (
                <FollowButton athleteId={networkAthlete.id} theme="dark" />
              )}
            </div>
          </div>
          <div className="md:text-right">
            <p className="font-body text-sm text-white/75">
              By <strong className="font-bold text-white">{story.author}</strong>
            </p>
            <p className="font-body text-sm text-white/45 mt-1">{formatDate(story.publish_date)}</p>
            {/* iter512 · Apple 1.2 UGC — Report / Block for stories.
                Any signed-in reader can report the story or block its
                author. Hidden for the story's own author. */}
            <div className="mt-2 md:flex md:justify-end">
              <ReportBlockMenu
                kind="story"
                targetId={story.id || story.slug}
                targetUserId={story.author_user_id}
                targetLabel={story.name}
                label="Report"
              />
            </div>
          </div>
        </div>
      </div>

      {/* "Visit the Network" pill — shows when this story is linked to a network athlete/visionary */}
      {networkAthlete && (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="visit-network-pill">
          <Link
            to={`${isVisionary ? '/visionary/network' : '/network'}/${networkAthlete.slug}`}
            className="group flex items-center justify-between gap-3 mt-6 p-4 rounded-2xl bg-gradient-to-br from-blue-600 via-blue-700 to-black text-white hover:from-blue-500 transition shadow-lg"
          >
            <div className="min-w-0">
              <p className="font-italic text-blue-200 text-[10px] uppercase tracking-widest mb-0.5">
                {isVisionary ? 'Dreamers Network' : 'Dreams League · Network'}
              </p>
              <p className="font-display text-lg uppercase truncate">Visit {networkAthlete.name}&apos;s Network Home</p>
              <p className="text-xs text-blue-100/80 mt-0.5 truncate">
                {isVisionary ? 'The build, the rooms, exclusive sit-down' : 'Brands, links, exclusive sit-down'}
              </p>
            </div>
            <span className="flex-shrink-0 w-10 h-10 rounded-full bg-white/15 group-hover:bg-white/25 flex items-center justify-center text-xl">→</span>
          </Link>
        </div>
      )}

      {/* Subtitle / Tagline */}
      {story.subtitle && (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 text-center">
          <h2 className="font-display text-3xl md:text-4xl uppercase tracking-wide">
            {story.subtitle}
          </h2>
        </div>
      )}

      {/* AD ZONE removed from top per editorial direction. Ads only live
          at the bottom of articles for now — see below the share row. */}

      {/* Article Body — matches the hero image width (max-w-5xl) so the
          pasted HTML reads end-to-end with the picture, with a tighter
          gap above (iter266 per founder request). */}
      <article className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 pb-8">
        {/* Modular content blocks render first when present — legacy
            article_body falls in below for backward compat. */}
        <ContentBlockRenderer
          blocks={story.content_blocks || []}
          channel={story.channel || 'dreams_league'}
        />
        <div 
          className="story-article-body" 
          dangerouslySetInnerHTML={{ __html: sanitizedArticleBody }}
          data-testid="article-body"
        />

        {/* Featured Brands — native sponsor block, only renders if the story
            has brand_features set. CMS-driven. */}
        <BrandIntegration features={story.brand_features || []} />

        {/* Inline Video / Full Story CTA */}
        {(story.video_url || story.full_story_url) && (
          <div className="my-16 text-center">
            {story.video_url && (
              <div className="video-embed mb-6">
                {isDirectVideo(story.video_url) ? (
                  <video
                    src={story.video_url.startsWith('/api/') ? `${BACKEND_URL}${story.video_url}` : story.video_url}
                    controls
                    playsInline
                    preload="metadata"
                    className="w-full"
                    data-testid="story-native-video"
                  />
                ) : (
                  <iframe
                    src={toVideoEmbed(story.video_url) || story.video_url}
                    title="Video"
                    frameBorder="0"
                    loading="lazy"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    data-testid="story-embed-video"
                  />
                )}
              </div>
            )}
            {story.full_story_url && (
              <a 
                href={story.full_story_url}
                target="_blank"
                rel="noopener noreferrer"
                className="font-body text-lg font-bold text-blue-400 hover:text-blue-300 transition inline-block"
                data-testid="watch-full-story-link"
              >
                Get Full Access →
              </a>
            )}
          </div>
        )}
      </article>

      {/* 3x3 Photo Gallery */}
      {gallery.length > 0 && (
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="story-gallery">
          <h2 className="font-display text-3xl md:text-4xl mb-8 uppercase text-center">Behind the Scenes</h2>
          <div className="grid grid-cols-3 gap-2 md:gap-3">
            {gallery.slice(0, 9).map((imgUrl, idx) => (
              <div 
                key={`gallery-${idx}-${imgUrl}`} 
                className="aspect-square overflow-hidden rounded-lg bg-white/5"
                data-testid={`gallery-image-${idx}`}
              >
                <img 
                  src={resolveImageUrl(imgUrl, 400)} 
                  alt={`Gallery ${idx + 1}`}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                />
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Bottom share buttons removed per editorial decision — the top share
          row in the article header is sufficient. Keeping the section
          structure clean for the AD ZONE below. */}

      {/* AD ZONE BOTTOM — disabled globally per iter151. Ads are off
          across the site so users get a clean read. The story editor
          still lets the founder attach slots (for a future re-enable),
          but nothing renders here. */}

      {/* Related Stories */}
      {relatedStories.length > 0 && (
        <section className="bg-[#0b0b0d] border-t border-white/10 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-display text-3xl mb-8 uppercase" data-testid="more-stories-heading">More Stories</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {relatedStories.map((relStory, idx) => (
                <div key={relStory.id} className="relative">
                  <Link
                    to={`${channel.storiesPath}/${relStory.slug}`}
                    className="bg-white/[0.04] ring-1 ring-white/10 hover:ring-white/25 rounded-2xl overflow-hidden story-card block transition"
                    data-testid={`related-story-${idx}`}
                  >
                    <img src={resolveImageUrl(relStory.thumbnail, 400)} alt={relStory.name} loading="lazy" decoding="async" className="w-full h-48 object-cover" />
                    <div className="p-6">
                      <span className="category-badge mb-3 inline-block">{relStory.category}</span>
                      <h3 className="font-body font-bold text-lg mb-2 leading-tight line-clamp-2 mt-3 text-white">{relStory.name}</h3>
                      <p className="text-sm text-white/50">{relStory.author} • {formatDate(relStory.publish_date)}</p>
                    </div>
                  </Link>
                  <CardSaveOverlay type="stories" id={relStory.id} />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default StoryPage;
